import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FiAlertCircle,
  FiArrowLeft,
  FiCheck,
  FiClock,
  FiCreditCard,
  FiFileText,
  FiGlobe,
  FiLoader,
  FiMapPin,
  FiTruck,
  FiUser,
} from "react-icons/fi";
import { BsCashCoin } from "react-icons/bs";

import {
  CartLayout,
  CheckoutForm,
  CheckoutSection,
  ErrorMessage,
  FormGrid,
  FormGroup,
  FormInput,
  FormLabel,
  FormSection,
  FormSectionTitle,
  FormTextarea,
  SectionTitle,
  SubmitButton,
} from "../CartPage.styles";
import CartOrderSummary, { CartTrustRow } from "./OrderSummary";
import { COUNTRY_LIST, PICKUP_INFO } from "../../../../utils/constants";
import { formatPrice } from "../../../../utils/utils";
import { getCountryConfig } from "../helpers";

const linkBackStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "0.4rem",
  color: "var(--text-secondary)",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  fontSize: "0.85rem",
  padding: "0.5rem 0",
  marginBottom: "1rem",
  transition: "color 0.2s ease",
};

const radioGroupStyle = {
  display: "flex",
  gap: "0.5rem",
  flexWrap: "wrap",
  marginBottom: "0.5rem",
};

const radioCardStyle = (selected, disabled) => ({
  flex: "1 1 200px",
  display: "flex",
  alignItems: "center",
  gap: "0.6rem",
  padding: "0.85rem 1rem",
  background: selected ? "var(--brand-dim)" : "rgba(255, 255, 255, 0.02)",
  border: `1px solid ${
    selected ? "rgba(255, 107, 53, 0.45)" : "var(--border-strong)"
  }`,
  borderRadius: "var(--radius-control)",
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.5 : 1,
  transition: "all 0.2s ease",
});

const pillStyle = (selected, disabled) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "0.4rem",
  padding: "0.4rem 0.75rem",
  background: selected ? "var(--brand-dim)" : "transparent",
  border: `1px solid ${
    selected ? "rgba(255, 107, 53, 0.45)" : "var(--border-strong)"
  }`,
  borderRadius: "var(--radius-pill)",
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.5 : 1,
  transition: "all 0.2s ease",
  fontSize: "0.85rem",
  lineHeight: 1.1,
  whiteSpace: "nowrap",
});

const hiddenRadioStyle = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
};

const customRadioStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 16,
  height: 16,
  borderRadius: "50%",
  border: "2px solid var(--brand)",
  background: "transparent",
  flexShrink: 0,
  transition: "all 0.2s ease",
};

const customRadioDotStyle = {
  width: 8,
  height: 8,
  borderRadius: "50%",
  background: "var(--brand)",
};

function CustomRadio({ selected }) {
  return (
    <span style={customRadioStyle} aria-hidden="true">
      {selected && <span style={customRadioDotStyle} />}
    </span>
  );
}

const helpTextStyle = {
  color: "var(--text-muted)",
  fontSize: "0.78rem",
  marginTop: "0.4rem",
  lineHeight: 1.5,
};

const legalTextStyle = {
  color: "var(--text-secondary)",
  fontSize: "13px",
  lineHeight: 1.5,
  margin: "0 0 0.75rem",
};

const legalLinkStyle = {
  color: "var(--text-primary)",
  textDecoration: "underline",
};

/* Blok sa podacima za lično preuzimanje (v2 s.13.5) */
const pickupBlockStyle = {
  background: "var(--brand-dim)",
  borderLeft: "3px solid var(--brand)",
  borderRadius: "0 var(--radius-control) var(--radius-control) 0",
  padding: "1rem 1.25rem",
  marginTop: "0.75rem",
  color: "var(--text-primary)",
  fontSize: "0.9rem",
  lineHeight: 1.6,
};

const stepMotion = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
  transition: { duration: 0.25 },
};

/* Stavka u pregledu porudžbine (v2 s.13.3) */
const summaryItemStyle = {
  display: "flex",
  alignItems: "center",
  gap: "0.7rem",
  padding: "0.5rem 0",
  borderBottom: "1px solid var(--border)",
};

export default function CheckoutStep({
  formData,
  errors,
  deliveryMethod,
  country,
  isPickupAvailable,
  totals,
  items = [],
  hasItems,
  isSubmitting,
  isSubmitSuccess,
  apiError,
  onInputChange,
  onCountryChange,
  onDeliveryMethodChange,
  onSubmit,
  onBack,
}) {
  const countryConfig = getCountryConfig(country);

  return (
    <motion.div {...stepMotion}>
      <button type="button" style={linkBackStyle} onClick={onBack}>
        <FiArrowLeft size={14} />
        Nazad na korpu
      </button>

      <CartLayout>
        <div>
          <CheckoutSection style={{ position: "static" }}>
            <SectionTitle>
              <FiCreditCard />
              Podaci za poručivanje
            </SectionTitle>

            <p style={{ ...helpTextStyle, marginTop: 0 }}>
              Sva polja su obavezna osim napomene.
            </p>

            <CheckoutForm onSubmit={onSubmit} noValidate>
              <FormSection>
                <FormSectionTitle>
                  <FiUser />
                  Lični podaci
                </FormSectionTitle>
                <FormGrid>
                  <FormGroup>
                    <FormLabel htmlFor="firstName">Ime *</FormLabel>
                    <FormInput
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={onInputChange}
                      placeholder="Tvoje ime"
                      className={errors.firstName ? "error" : ""}
                    />
                    {errors.firstName && (
                      <ErrorMessage data-field-error="firstName">
                        <FiAlertCircle size={12} />
                        {errors.firstName}
                      </ErrorMessage>
                    )}
                  </FormGroup>

                  <FormGroup>
                    <FormLabel htmlFor="lastName">Prezime *</FormLabel>
                    <FormInput
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={onInputChange}
                      placeholder="Tvoje prezime"
                      className={errors.lastName ? "error" : ""}
                    />
                    {errors.lastName && (
                      <ErrorMessage data-field-error="lastName">
                        <FiAlertCircle size={12} />
                        {errors.lastName}
                      </ErrorMessage>
                    )}
                  </FormGroup>
                </FormGrid>

                <FormGrid>
                  <FormGroup>
                    <FormLabel htmlFor="email">Email *</FormLabel>
                    <FormInput
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={onInputChange}
                      placeholder="tvoj@email.rs"
                      className={errors.email ? "error" : ""}
                    />
                    {errors.email ? (
                      <ErrorMessage data-field-error="email">
                        <FiAlertCircle size={12} />
                        {errors.email}
                      </ErrorMessage>
                    ) : (
                      <p style={{ ...helpTextStyle, marginTop: "0.3rem" }}>
                        Na ovaj email stiže potvrda porudžbine.
                      </p>
                    )}
                  </FormGroup>

                  <FormGroup>
                    <FormLabel htmlFor="phone">Telefon *</FormLabel>
                    <FormInput
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={onInputChange}
                      placeholder={`${countryConfig.phonePrefix} ${countryConfig.phonePlaceholder}`}
                      className={errors.phone ? "error" : ""}
                    />
                    {errors.phone && (
                      <ErrorMessage data-field-error="phone">
                        <FiAlertCircle size={12} />
                        {errors.phone}
                      </ErrorMessage>
                    )}
                  </FormGroup>
                </FormGrid>
              </FormSection>

              <FormSection>
                <FormSectionTitle>
                  <FiGlobe />
                  Država dostave
                </FormSectionTitle>

                <FormGroup>
                  <div style={radioGroupStyle}>
                    {COUNTRY_LIST.map((c) => (
                      <label
                        key={c.code}
                        style={pillStyle(country === c.code, false)}
                      >
                        <input
                          type="radio"
                          name="country"
                          value={c.code}
                          checked={country === c.code}
                          onChange={(e) => onCountryChange(e.target.value)}
                          style={hiddenRadioStyle}
                        />
                        <CustomRadio selected={country === c.code} />
                        <span
                          style={{
                            color:
                              country === c.code
                                ? "var(--text-primary)"
                                : "var(--text-secondary)",
                            fontWeight: country === c.code ? 600 : 500,
                          }}
                        >
                          {c.name}
                        </span>
                      </label>
                    ))}
                  </div>
                  {/* Padeži iz konfiguracije, "za Srbiju / za Crnu Goru" */}
                  <p style={helpTextStyle}>
                    {countryConfig.deliveryPriceEur
                      ? `Poštarina za ${countryConfig.nameGenitive}: ≈ ${countryConfig.deliveryPriceEur} € (≈ ${formatPrice(countryConfig.deliveryPrice)}) po paketu.`
                      : `Poštarina za ${countryConfig.nameGenitive}: ≈ ${formatPrice(countryConfig.deliveryPrice)} po paketu${countryConfig.freeShippingThreshold ? ` (besplatno preko ${formatPrice(countryConfig.freeShippingThreshold)} po pošiljci)` : ""}.`}
                  </p>
                </FormGroup>
              </FormSection>

              <FormSection>
                <FormSectionTitle>
                  <FiMapPin />
                  Način dostave
                </FormSectionTitle>

                <FormGroup>
                  <div style={radioGroupStyle}>
                    <label
                      style={radioCardStyle(
                        deliveryMethod === "delivery",
                        false,
                      )}
                    >
                      <input
                        type="radio"
                        name="deliveryMethod"
                        value="delivery"
                        checked={deliveryMethod === "delivery"}
                        onChange={(e) => onDeliveryMethodChange(e.target.value)}
                        style={hiddenRadioStyle}
                      />
                      <CustomRadio selected={deliveryMethod === "delivery"} />
                      <span
                        style={{ color: "var(--text-primary)", fontWeight: 600 }}
                      >
                        Dostava na kućnu adresu
                      </span>
                    </label>
                    <label
                      style={radioCardStyle(
                        deliveryMethod === "pickup",
                        !isPickupAvailable,
                      )}
                    >
                      <input
                        type="radio"
                        name="deliveryMethod"
                        value="pickup"
                        checked={deliveryMethod === "pickup"}
                        onChange={(e) => onDeliveryMethodChange(e.target.value)}
                        disabled={!isPickupAvailable}
                        style={hiddenRadioStyle}
                      />
                      <CustomRadio selected={deliveryMethod === "pickup"} />
                      <span
                        style={{ color: "var(--text-primary)", fontWeight: 600 }}
                      >
                        Pokupiću u {PICKUP_INFO.city}u
                      </span>
                    </label>
                  </div>

                  {/* Bez internog žargona (v2 s.13.5) */}
                  {!isPickupAvailable ? (
                    <p style={helpTextStyle}>
                      {!countryConfig.pickupAllowed
                        ? "Lično preuzimanje je moguće samo uz dostavu u Srbiji."
                        : "Lično preuzimanje nije dostupno za proizvode u tvojoj korpi."}
                    </p>
                  ) : (
                    <p style={helpTextStyle}>
                      Lično preuzimanje je moguće za ovu porudžbinu.
                    </p>
                  )}

                  {/* Očekivano vreme isporuke po državi */}
                  {deliveryMethod === "delivery" && (
                    <p
                      style={{
                        ...helpTextStyle,
                        display: "flex",
                        alignItems: "center",
                        gap: "0.35rem",
                      }}
                    >
                      <FiTruck size={13} style={{ color: "var(--brand)" }} />
                      Isporuka: {countryConfig.deliveryEstimate}
                    </p>
                  )}
                </FormGroup>

                {/* Adresa preuzimanja kad je izabrano lično (v2 s.13.5) */}
                {deliveryMethod === "pickup" && (
                  <div style={pickupBlockStyle}>
                    <div style={{ fontWeight: 700, marginBottom: "0.25rem" }}>
                      Preuzimanje: {PICKUP_INFO.city}
                    </div>
                    <div>{PICKUP_INFO.address}</div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.35rem",
                      }}
                    >
                      <FiClock size={13} /> Radno vreme: {PICKUP_INFO.hours}
                    </div>
                    <div
                      style={{
                        color: "var(--text-secondary)",
                        fontSize: "0.82rem",
                        marginTop: "0.35rem",
                      }}
                    >
                      {PICKUP_INFO.note}
                    </div>
                  </div>
                )}

                {deliveryMethod === "delivery" && (
                  <>
                    <FormGroup style={{ marginTop: "1rem" }}>
                      <FormLabel htmlFor="address">Adresa *</FormLabel>
                      <FormInput
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={onInputChange}
                        placeholder="Ulica i broj"
                        className={errors.address ? "error" : ""}
                      />
                      {errors.address && (
                        <ErrorMessage data-field-error="address">
                          <FiAlertCircle size={12} />
                          {errors.address}
                        </ErrorMessage>
                      )}
                    </FormGroup>

                    <FormGrid>
                      <FormGroup>
                        <FormLabel htmlFor="city">Grad *</FormLabel>
                        <FormInput
                          id="city"
                          name="city"
                          value={formData.city}
                          onChange={onInputChange}
                          placeholder="Grad"
                          className={errors.city ? "error" : ""}
                        />
                        {errors.city && (
                          <ErrorMessage data-field-error="city">
                            <FiAlertCircle size={12} />
                            {errors.city}
                          </ErrorMessage>
                        )}
                      </FormGroup>

                      <FormGroup>
                        <FormLabel htmlFor="zipCode">
                          Poštanski broj *
                        </FormLabel>
                        <FormInput
                          id="zipCode"
                          name="zipCode"
                          value={formData.zipCode}
                          onChange={onInputChange}
                          placeholder="34000"
                          className={errors.zipCode ? "error" : ""}
                        />
                        {errors.zipCode && (
                          <ErrorMessage data-field-error="zipCode">
                            <FiAlertCircle size={12} />
                            {errors.zipCode}
                          </ErrorMessage>
                        )}
                      </FormGroup>
                    </FormGrid>
                  </>
                )}
              </FormSection>

              {/* NAČIN PLAĆANJA (v2 s.13.1) */}
              <FormSection>
                <FormSectionTitle>
                  <BsCashCoin />
                  Način plaćanja
                </FormSectionTitle>
                <FormGroup>
                  <div style={radioGroupStyle}>
                    <label style={radioCardStyle(true, false)}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="cod"
                        checked
                        readOnly
                        style={hiddenRadioStyle}
                      />
                      <CustomRadio selected />
                      <span>
                        <span
                          style={{
                            display: "block",
                            color: "var(--text-primary)",
                            fontWeight: 600,
                          }}
                        >
                          Plaćanje pouzećem
                        </span>
                        <span
                          style={{
                            display: "block",
                            color: "var(--text-secondary)",
                            fontSize: "13px",
                            marginTop: "0.15rem",
                          }}
                        >
                          Gotovinom kuriru prilikom preuzimanja paketa.
                        </span>
                      </span>
                    </label>
                  </div>
                </FormGroup>
              </FormSection>

              <FormSection>
                <FormSectionTitle>
                  <FiFileText />
                  Napomena
                </FormSectionTitle>

                <FormGroup>
                  <FormTextarea
                    id="details"
                    name="details"
                    value={formData.details}
                    onChange={onInputChange}
                    placeholder="Dodatne napomene za porudžbinu (opciono)"
                  />
                </FormGroup>
              </FormSection>

              {/* Greška API-ja, inline iznad dugmeta, podaci ostaju */}
              {apiError && (
                <ErrorMessage
                  style={{
                    marginBottom: "0.75rem",
                    padding: "0.75rem 1rem",
                    background: "rgba(220, 53, 69, 0.1)",
                    border: "1px solid rgba(220, 53, 69, 0.35)",
                    borderRadius: "var(--radius-control)",
                  }}
                >
                  <FiAlertCircle size={16} />
                  {apiError}
                </ErrorMessage>
              )}

              {/* Pravna zona (v2 s.13.4) */}
              <p style={legalTextStyle}>
                Klikom na dugme poručuješ uz obavezu plaćanja i prihvataš{" "}
                <Link to="/uslovi-kupovine" style={legalLinkStyle}>
                  Uslove kupovine
                </Link>{" "}
                i{" "}
                <Link to="/politika-privatnosti" style={legalLinkStyle}>
                  Politiku privatnosti
                </Link>
                . Imaš pravo na odustanak od ugovora u roku od 14 dana od
                prijema.
              </p>

              <SubmitButton
                type="submit"
                className={`${isSubmitting ? "loading" : ""} ${
                  isSubmitSuccess ? "success" : ""
                }`}
                disabled={isSubmitting || !hasItems}
              >
                {isSubmitting ? (
                  <>
                    <FiLoader />
                    Slanje...
                  </>
                ) : isSubmitSuccess ? (
                  <>
                    <FiCheck />
                    Porudžbina je poslana!
                  </>
                ) : (
                  <>
                    <FiCheck />
                    Poruči uz obavezu plaćanja
                  </>
                )}
              </SubmitButton>

              <CartTrustRow />
            </CheckoutForm>
          </CheckoutSection>
        </div>

        <div>
          <CheckoutSection>
            <SectionTitle>
              <FiCreditCard />
              Pregled porudžbine
            </SectionTitle>

            {/* Lista stavki (v2 s.13.3), izmene idu kroz korpu */}
            {items.length > 0 && (
              <div style={{ marginBottom: "1rem" }}>
                {items.map((item) => (
                  <div key={item.cartItemId} style={summaryItemStyle}>
                    <img
                      src={item.mainImage}
                      alt=""
                      style={{
                        width: 48,
                        height: 48,
                        objectFit: "cover",
                        borderRadius: "var(--radius-control)",
                        border: "1px solid var(--border)",
                        background: "var(--bg-elevated)",
                        flexShrink: 0,
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          color: "var(--text-primary)",
                          fontSize: "0.88rem",
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {item.name}
                      </div>
                      <div
                        style={{
                          color: "var(--text-secondary)",
                          fontSize: "13px",
                        }}
                      >
                        {item.selectedSize ? `Vel. ${item.selectedSize} · ` : ""}
                        {item.quantity} kom
                      </div>
                    </div>
                    <div
                      style={{
                        color: "var(--text-primary)",
                        fontSize: "0.88rem",
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {formatPrice(
                        (item.salePrice || item.price) * item.quantity,
                      )}
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={onBack}
                  style={{
                    ...linkBackStyle,
                    marginBottom: 0,
                    fontSize: "13px",
                  }}
                >
                  <FiArrowLeft size={13} />
                  Izmeni korpu
                </button>
              </div>
            )}

            <CartOrderSummary
              totals={totals}
              country={country}
              hasItems={hasItems}
              showShipping
              paymentRow
            />
          </CheckoutSection>
        </div>
      </CartLayout>
    </motion.div>
  );
}
